import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getNotifications, markAllNotificationsRead, type NotificationApi } from '../../../api/client';
import { formatRelativeTime } from '../../../utils/date';

const NAV_TABS = [
  { id: 'playlist', label: 'Playlist', icon: 'ri-play-list-fill' },
  { id: 'manage', label: 'Manage', icon: 'ri-folder-music-fill' },
  { id: 'recommendations', label: 'Discover', icon: 'ri-compass-fill' },
  { id: 'subscribers', label: 'Followers', icon: 'ri-user-heart-fill' },
] as const;

interface TopBarProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
}

export default function TopBar({ activeTab, setActiveTab }: TopBarProps) {
  const navigate = useNavigate();
  const { isAdmin, isLoggedIn, username, logout, avatarUrl } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [qualityHD, setQualityHD] = useState(true);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const showProfileImage = avatarUrl && !profileImageError;
  const [notifications, setNotifications] = useState<NotificationApi[]>([]);

  useEffect(() => {
    if (!isLoggedIn) {
      setNotifications([]);
      return;
    }
    getNotifications().then(setNotifications).catch(() => setNotifications([]));
  }, [isLoggedIn]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleMarkAllRead = () => {
    markAllNotificationsRead()
      .then(() => setNotifications((prev) => prev.map((n) => ({ ...n, read: true }))))
      .catch(() => {});
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-stone-200/70 bg-white/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:hidden py-3">
          <div className="flex items-center justify-between gap-3">
            <a href="/" className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-200 to-orange-200">
                <img src="/logo.png" alt="Frequency Locus" className="h-6 w-6 object-contain" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-base font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                  Frequency Locus
                </h1>
                <p className="truncate text-xs text-stone-500">
                  Music, radio, stories
                </p>
              </div>
            </a>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative flex h-10 w-10 items-center justify-center rounded-2xl bg-stone-100 text-stone-700"
              >
                <i className="ri-notification-line text-base" />
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-700 text-[10px] text-white">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowAccountMenu(!showAccountMenu)}
                className={`flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl ${showProfileImage ? 'ring-2 ring-amber-200' : 'bg-stone-100 text-stone-700'}`}
              >
                {showProfileImage ? (
                  <img src={avatarUrl!} alt="Profile" className="h-full w-full object-cover" onError={() => setProfileImageError(true)} />
                ) : (
                  <i className="ri-user-line text-lg" aria-hidden />
                )}
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search tracks, playlists, people"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-2xl border border-stone-200 bg-stone-50 px-4 py-2.5 pl-10 text-sm text-stone-700 focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
            </div>
            {!isLoggedIn && (
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="rounded-2xl bg-amber-700 px-4 py-2.5 text-sm font-medium text-white"
              >
                Login
              </button>
            )}
          </div>

          {showNotifications && (
            <div className="mt-3 rounded-3xl border border-stone-200 bg-white p-3 shadow-lg">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-stone-900">Notifications</h3>
                <button type="button" onClick={handleMarkAllRead} className="text-xs font-medium text-amber-700">
                  Mark all read
                </button>
              </div>
              <div className="space-y-2">
                {notifications.length === 0 ? (
                  <p className="rounded-2xl bg-stone-50 px-3 py-4 text-sm text-stone-500">No notifications</p>
                ) : (
                  notifications.slice(0, 4).map((notification) => (
                    <div key={notification.id} className={`rounded-2xl px-3 py-3 text-sm ${notification.read ? 'bg-stone-50' : 'bg-amber-50'}`}>
                      <p className="text-stone-800">{notification.message}</p>
                      <p className="mt-1 text-xs text-stone-500">{formatRelativeTime(notification.time)}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {showAccountMenu && (
            <div className="mt-3 rounded-3xl border border-stone-200 bg-white p-2 shadow-lg">
              {isLoggedIn && isAdmin && (
                <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/admin'); }} className="flex w-full items-center gap-2 rounded-2xl px-3 py-3 text-left text-sm font-medium text-amber-800 hover:bg-amber-50">
                  <i className="ri-shield-user-line" aria-hidden />
                  Admin
                </button>
              )}
              <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/profile'); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50">
                Profile
              </button>
              <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/pricing'); }} className="flex w-full items-center gap-2 rounded-2xl px-3 py-3 text-left text-sm hover:bg-amber-50 text-amber-700 font-medium">
                <i className="ri-vip-crown-line" aria-hidden />
                Upgrade to Pro
              </button>
              {isLoggedIn && (
                <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/account/billing'); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50 text-stone-600">
                  Billing
                </button>
              )}
              {isLoggedIn ? (
                <button type="button" onClick={() => { setShowAccountMenu(false); logout(); setTimeout(() => navigate('/'), 0); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50">
                  Logout
                </button>
              ) : (
                <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/register'); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50">
                  Create account
                </button>
              )}
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-6 py-4">
          <a href="/" className="flex items-center gap-3 shrink-0">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-200 to-orange-200">
              <img src="/logo.png" alt="Frequency Locus" className="h-7 w-7 object-contain" />
            </div>
            <div>
              <p className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                Frequency Locus
              </p>
              <p className="text-xs text-stone-500">Radio, playlists, culture</p>
            </div>
          </a>

          {setActiveTab && (
            <nav className="flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 p-1">
              {NAV_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          )}

          <div className="relative ml-auto w-full max-w-sm">
            <input
              type="text"
              placeholder="Search tracks, playlists, people"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-full border border-stone-200 bg-stone-50 px-4 py-2.5 pl-10 text-sm text-stone-700 focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isLoggedIn ? (
              <span className="rounded-full bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800" title={username ?? ''}>
                {username}
              </span>
            ) : (
              <button type="button" onClick={() => navigate('/login')} className="rounded-full bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800">
                Login
              </button>
            )}

            <button
              type="button"
              onClick={() => setQualityHD(!qualityHD)}
              className={`rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                qualityHD ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600'
              }`}
            >
              {qualityHD ? 'HD' : 'SD'}
            </button>

            <div className="relative">
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-stone-100 text-stone-700 hover:bg-stone-200">
                <i className="ri-notification-line text-lg" />
                {unreadCount > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-700 text-[10px] text-white">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 rounded-3xl border border-stone-200 bg-white p-3 shadow-xl z-20">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-semibold text-stone-900">Notifications</div>
                    <button type="button" onClick={handleMarkAllRead} className="text-xs font-medium text-amber-700">
                      Mark all read
                    </button>
                  </div>
                  <div className="space-y-2">
                    {notifications.length === 0 ? (
                      <p className="rounded-2xl bg-stone-50 px-3 py-4 text-sm text-stone-500">No notifications</p>
                    ) : (
                      notifications.slice(0, 4).map((n) => (
                        <div key={n.id} className={`rounded-2xl px-3 py-3 text-sm ${n.read ? 'bg-stone-50' : 'bg-amber-50'}`}>
                          <p className="text-stone-800">{n.message}</p>
                          <p className="mt-1 text-xs text-stone-500">{formatRelativeTime(n.time)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button type="button" onClick={() => setShowAccountMenu(!showAccountMenu)} className={`flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl ${showProfileImage ? 'ring-2 ring-amber-200' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} title="Account menu">
                {showProfileImage ? (
                  <img src={avatarUrl!} alt="Account menu" className="h-full w-full object-cover" onError={() => setProfileImageError(true)} />
                ) : (
                  <i className="ri-user-line text-xl" aria-hidden />
                )}
              </button>
              {showAccountMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-3xl border border-stone-200 bg-white p-2 shadow-xl z-20">
                  {isLoggedIn && isAdmin && (
                    <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/admin'); }} className="flex w-full items-center gap-2 rounded-2xl px-3 py-3 text-left text-sm font-medium text-amber-800 hover:bg-amber-50">
                      <i className="ri-shield-user-line" aria-hidden />
                      Admin
                    </button>
                  )}
                  <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/profile'); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50">
                    Profile
                  </button>
                  <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/pricing'); }} className="flex w-full items-center gap-2 rounded-2xl px-3 py-3 text-left text-sm hover:bg-amber-50 text-amber-700 font-medium">
                    <i className="ri-vip-crown-line" aria-hidden />
                    Upgrade to Pro
                  </button>
                  {isLoggedIn && (
                    <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/account/billing'); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50 text-stone-600">
                      Billing
                    </button>
                  )}
                  {isLoggedIn ? (
                    <button type="button" onClick={() => { setShowAccountMenu(false); logout(); setTimeout(() => navigate('/'), 0); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50">
                      Logout
                    </button>
                  ) : (
                    <button type="button" onClick={() => { setShowAccountMenu(false); navigate('/register'); }} className="w-full rounded-2xl px-3 py-3 text-left text-sm hover:bg-stone-50">
                      Create account
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
